import React, { useState, useEffect } from 'react';
import {
  usePlayerStore,
  useExamSessionStore,
  getStore,
} from '../../../stores/StoreContext';
import { View, Text } from '@smarterservices/smarter-ui';
import NavigationalMenuItem from '@ui/shared/components/Common/NavigationalMenuItem';
import { Pill, Tooltip, Popover } from '@instructure/ui';
import { formatTime } from '../../../helpers/utilities';
import { useFullScreenMountNode } from '../../../contexts/FullScreenContext';
import CONSTANTS from '../../../config/constants';
const PlaybarTimer = () => {
  const PlayerStore = getStore('playerStore');
  const isLive = usePlayerStore((state) => state.isLive);
  const recordingStatus = useExamSessionStore((state) => state.recordingStatus);
  const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
  const smartSpeedEnabled = usePlayerStore((state) => state.smartSpeedEnabled);

  const speedOptions = usePlayerStore((state) => state.speedOptions);
  const mountNode = useFullScreenMountNode();

  const [width, setWidth] = useState(110);
  const [showPlaybackSpeedIndicator, setShowPlaybackSpeedIndicator] = useState(false);
  const [isSpeedPopoverOpen, setIsSpeedPopoverOpen] = useState(false);

  useEffect(() => {
    const numericSpeed = parseFloat(playbackSpeed);

    if ((isLive || !smartSpeedEnabled) && numericSpeed === 1) {
      setWidth(110);
      setShowPlaybackSpeedIndicator(false);
    } else {
      setWidth(130);
      setShowPlaybackSpeedIndicator(true);
    }
  }, [isLive, smartSpeedEnabled, playbackSpeed]);

  const speedDisplay = () => {
    if (showPlaybackSpeedIndicator) {
      const numericSpeed = parseFloat(playbackSpeed);
      const formatted = Number.isInteger(numericSpeed) ? numericSpeed : numericSpeed.toString();
      return `@ ${formatted}x`;
    }
    return "";
  };

  const handleSpeedSelect = (value) => {
    PlayerStore.setPlaybackSpeed(value);
    setIsSpeedPopoverOpen(false);
  };

  const renderSpeedOptions = () => (
    <View as="div" padding="x-small" width="210px">
      <View as="div" padding="x-small small" background='secondary' borderRadius='medium'>
        <Text as="div" weight="bold">
          Playback Speed
        </Text>
      </View>
      {speedOptions.map((option) => {
        const effectiveSpeed = smartSpeedEnabled ? '100.0' : playbackSpeed;
        const isActive = parseFloat(effectiveSpeed) === option.value;
        return (
          <View
            as="div"
            key={option.key}
            padding="xx-small small"
            cursor="pointer"
            background={isActive ? 'brand' : 'primary'}
            borderRadius="small"
            onClick={() => handleSpeedSelect(option.value)}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = '';
            }}
          >
            <NavigationalMenuItem
              shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.PLAYBACK?.OPTIONS?.[option.key.toUpperCase()]?.KEY}
              textSize="small"
              textColor={isActive ? 'primary-inverse' : 'primary'}
              textWeight={isActive ? 'bold' : 'normal'}
            >
              {option.label}
            </NavigationalMenuItem>
          </View>
        );
      })}
    </View>
  );


  /**
   * This function displays the time of the video in the player's control bar.
   * If the video is live, it displays "Live" followed by the length of the active session.
   * If the video is not live, it displays the current time and the duration of the video.
   * This is done this way so we can limit the re-renders to the most minimal amount of the dom
   * @function
   * @return {string} A string representing the time of the video.
   */
  const getDisplayText = () => {
    const activeSessionTime = usePlayerStore(
      (state) => state.activeSessionTime
    );
    const duration = usePlayerStore((state) => state.duration);
    const currentTime = usePlayerStore((state) => state.currentTime);
    let text = "";
    if (isLive) {
      text = `Live ${activeSessionTime ? '/ ' + formatTime(activeSessionTime) : ''}`;
    } else {
      text = `${formatTime(currentTime)} / ${formatTime(duration)} ${speedDisplay()}`;
    }

    return <Text
      size="legend"
      themeOverride={{
        legend: '0.65rem'
      }}>{text}</Text>;

  };

  const renderToolTip = () => {
    return `The end of the recorded video is ${PlayerStore.secondsBehindLive} seconds behind live.`;
  };

  return (
    <View as="div" width={`${width}px`} display="inline-block" textAlign="center">
      {isLive ? (
        <Pill color="success"
          themeOverride={(theme) => {
            return {
              background: theme.successColor,
              successColor: 'white'
            };
          }}>
          {getDisplayText()}
        </Pill>
      ) : (
        <Popover
          renderTrigger={
            recordingStatus === CONSTANTS.RECORDING_STATUS.COMPLETE ? (
              <Pill color="warning" cursor="pointer">
                {getDisplayText()}
              </Pill>
            ) : (
              <Pill color="warning" cursor="pointer" themeOverride={(theme) => ({
                background: theme.dangerColor,
                warningColor: 'white'
              })}>
                {getDisplayText()}
              </Pill>
            )
          }
          isShowingContent={isSpeedPopoverOpen}
          onShowContent={() => setIsSpeedPopoverOpen(true)}
          onHideContent={() => setIsSpeedPopoverOpen(false)}
          on="click"
          screenReaderLabel="Playback speed selector"
          shouldContainFocus={false}
          shouldReturnFocus
          shouldCloseOnDocumentClick
          placement="top"
          mountNode={() => mountNode}
        >
          {renderSpeedOptions()}
        </Popover>
      )
      }
    </View >
  );
};

export default PlaybarTimer;
